import useSWR from 'swr';

import { fetchCameraConditions } from 'common/api/kbyg';
import type { CameraConditionsResponse } from 'types/camera';

interface UseCameraMatchConditionsParams {
  spotId: string | undefined;
  enabled: boolean;
  camTimestampSeconds: number | null;
  forecastTimestampSeconds: number | null;
}

const fetcher = ([, spotId, timestamp]: [string, string, number]) =>
  fetchCameraConditions({ id: spotId, timestamp });

const CAMERA_MATCH_CONDITIONS_SWR_OPTIONS = {
  errorRetryCount: 0,
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnMount: true,
  revalidateOnReconnect: false,
};

export function useCameraMatchConditions({
  spotId,
  enabled,
  camTimestampSeconds,
  forecastTimestampSeconds,
}: UseCameraMatchConditionsParams): {
  cam: CameraConditionsResponse | undefined;
  forecast: CameraConditionsResponse | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const camKey =
    enabled && spotId && camTimestampSeconds != null
      ? (['camera-conditions', spotId, camTimestampSeconds] as const)
      : null;

  const forecastKey =
    enabled && spotId && forecastTimestampSeconds != null
      ? (['camera-conditions', spotId, forecastTimestampSeconds] as const)
      : null;

  const {
    data: cam,
    error: camError,
    isLoading: camLoading,
  } = useSWR(camKey, fetcher, CAMERA_MATCH_CONDITIONS_SWR_OPTIONS);

  const {
    data: forecast,
    error: forecastError,
    isLoading: forecastLoading,
  } = useSWR(forecastKey, fetcher, CAMERA_MATCH_CONDITIONS_SWR_OPTIONS);

  return {
    cam,
    forecast,
    isLoading: Boolean(camLoading || forecastLoading),
    error: camError ?? forecastError,
  };
}

export default useCameraMatchConditions;
